// The modes (workspace-modes note §3, design spec 2026-10-19 §2–§3; decision 2026-10-19: mode tabs
// centred in the top bar, all visible, unbuilt modes show a being-built page). Five lenses on one
// company (KPIS-MODE, founder-approved 2026-10-22: Chart and Cadence became one mode, KPIs), each
// with its key. NAV-REDESIGN (founder-approved 2026-10-29): the top tabs are the work modes —
// Chat, Work, Calendar, KPIs; the rail holds Home, Inbox and Live (was Screens) on top, Workforce,
// Settings (Setup) and Me at its foot; Reports is a section of KPIs (`/kpis/reports`). Every key
// stays: 1 Chat, 2 Work, 3 Live, 4 Calendar, 5 KPIs, 6 Workforce, 7 Reports, and each `g` chord.
// The registry is the one source of truth for the tabs, the rail, the phone bar, the router
// guard, ⌘K and the keys.
import {
  BotIcon,
  CalendarDaysIcon,
  FileTextIcon,
  GaugeIcon,
  LampDeskIcon,
  MessageSquareIcon,
  MonitorIcon,
  SettingsIcon,
  SquareKanbanIcon,
  TelescopeIcon,
} from "lucide-react";
import type { CompanyRole, MeResponse } from "../api/types";
import type { CompanyAccess } from "../features/company";
import { featureState } from "../features/use-feature";
import { AgentsSidebar } from "../shell/sidebars/agents";
import { CalendarSidebar } from "../shell/sidebars/calendar";
import { ChatSidebar } from "../shell/sidebars/chat";
import { HomeSidebar } from "../shell/sidebars/home";
import { KpisSidebar } from "../shell/sidebars/kpis";
import { PortfolioSidebar } from "../shell/sidebars/portfolio";
import { ReportsSidebar } from "../shell/sidebars/reports";
import { SetupSidebar } from "../shell/sidebars/setup";
import { WorkSidebar } from "../shell/sidebars/work";
import { ExtensionPointRegistry, type ModeEntry } from "./registry";

// MEMBER-ROLE (05 §Model `company`): who sees which mode.
//   MANAGERS  owner | admin | agency_operator (company#manager): Setup;
//   MEMBERS   managers | member (company#member): Chat, Work, KPIs › Chart — everyone but a
//             pure client viewer (an agency viewer grant reads as one, features/company.ts);
//   READERS   managers | client_viewer (company#reader): KPIs › Scorecard (the `cadence` gate)
//             and Reports read the company's KPI roll-up, reports and costs, which a plain
//             member does not (05: scorecards are its own seat's: KPIs › My scorecard);
//   EVERYONE  all of them: Agents (My agent, approvals, runs; the roster is `company#viewer`)
//             and Home (MEMBER-UX): a personal start page for everyone; the company tiles,
//             scorecard and Friday report on it are company#reader's (home-screen.tsx).
const MANAGERS: CompanyRole[] = ["owner", "admin", "agency_operator"];
const MEMBERS: CompanyRole[] = [...MANAGERS, "member"];
const READERS: CompanyRole[] = [...MANAGERS, "client_viewer"];
const EVERYONE: CompanyRole[] = [...MANAGERS, "member", "client_viewer"];

export const extensionPoints = new ExtensionPointRegistry()
  .register("mode", {
    // Desk (design spec §5, founder decision 2; SHELL-DECISIONS 2026-10-30: Home was renamed Desk,
    // `/home` redirects to `/desk`): the landing of managers, operators and (MEMBER-UX) plain
    // members; client viewers keep landing on Reports and open Desk from the rail.
    id: "mode:desk",
    publisher: "companyos",
    key: "desk",
    label: "Desk",
    railHint: "Your desk: today's tasks, approvals, briefs",
    icon: LampDeskIcon,
    path: "/c/$slug/desk",
    feature: "core",
    overlayFeatures: [],
    roles: EVERYONE,
    defaultFor: ["owner", "admin", "agency_operator", "member"],
    shortcut: "d",
    mobileOrder: 0,
    placement: "global",
    sidebar: HomeSidebar,
    landing: true,
  })
  .register("mode", {
    // FOUNDER-OS (12): the CEO-level view over the account's companies — Focus, the portfolio
    // chart, goals, escalated issues, the brief and the thinking space. Only in the founder's
    // Founder OS company (`kind = portfolio`), where it is the landing.
    id: "mode:portfolio",
    publisher: "companyos",
    key: "portfolio",
    label: "Portfolio",
    icon: TelescopeIcon,
    path: "/c/$slug/portfolio",
    feature: "core",
    overlayFeatures: [],
    roles: EVERYONE,
    defaultFor: EVERYONE,
    companyKinds: ["portfolio"],
    shortcut: "f",
    mobileOrder: 0.5,
    placement: "tab",
    sidebar: PortfolioSidebar,
  })
  .register("mode", {
    // Base product (note §3): channels, topics, DMs, agent rooms (M2-06). The pages also need the
    // `chat` add-on (the route's gate); the tab stays, so "not enabled" explains itself.
    id: "mode:chat",
    publisher: "companyos",
    key: "chat",
    label: "Chat",
    icon: MessageSquareIcon,
    path: "/c/$slug/chat",
    feature: "core",
    overlayFeatures: [],
    roles: MEMBERS,
    defaultFor: [],
    shortcut: "c",
    digit: "1",
    // UX-A (ux pass 2.4): Chat and Work, what a member opens most, lead the phone's bottom bar.
    mobileOrder: 1,
    placement: "tab",
    sidebar: ChatSidebar,
  })
  .register("mode", {
    // Base product (note §3): spaces, boards and tasks (M2-07); pages come with M7-02.
    id: "mode:work",
    publisher: "companyos",
    key: "work",
    label: "Work",
    icon: SquareKanbanIcon,
    path: "/c/$slug/work",
    feature: "core",
    overlayFeatures: [],
    roles: MEMBERS,
    defaultFor: [],
    shortcut: "w",
    digit: "2",
    mobileOrder: 2,
    placement: "tab",
    sidebar: WorkSidebar,
  })
  .register("mode", {
    // SCREENS-S1 (docs/design/2026-10-28-browser-computer-decision.md §4, §7.5): the company's
    // computer — its agents' live browsers and desktops, watch / take over / hand back, the
    // Waiting list and the browser logins. Screens exist for agents: the `agents` add-on.
    // NAV-REDESIGN: renamed Live and moved to the rail (`/screens` redirects); key 3 and `g s` stay.
    id: "mode:live",
    publisher: "companyos",
    key: "live",
    label: "Live",
    icon: MonitorIcon,
    path: "/c/$slug/live",
    feature: "agents",
    overlayFeatures: [],
    roles: MEMBERS,
    defaultFor: [],
    shortcut: "s",
    digit: "3",
    mobileOrder: 0.2,
    placement: "global",
  })
  .register("mode", {
    // CALENDAR-C1 (docs/design/2026-10-28-calendar.md §3, decision 1: a top-level entry): the
    // company's, my teams' and my own calendars overlaid, with people who shared theirs, day /
    // week / month / agenda and the tasks due as read-only chips. A member's: client viewers
    // read the company's numbers, not its diary. Third tab, right after Work (MODE-ORDER).
    id: "mode:calendar",
    publisher: "companyos",
    key: "calendar",
    label: "Calendar",
    icon: CalendarDaysIcon,
    path: "/c/$slug/calendar",
    feature: "core",
    overlayFeatures: [],
    roles: MEMBERS,
    defaultFor: [],
    shortcut: "l",
    digit: "4",
    mobileOrder: 5.5,
    placement: "tab",
    sidebar: CalendarSidebar,
  })
  .register("mode", {
    // KPIs (founder-approved 2026-10-22; was Chart + Cadence): one mode for the accountability
    // chart and the numbers on it — Chart (the org chart, its dimension tabs and drawer),
    // Scorecard (the EOS grid), My numbers, Priorities, Issues, Meetings, Vision and data entry.
    // Its pages keep their own audiences through the two gates below (`chart`: every member;
    // `cadence`: the company's KPI readers, a plain member only on its own pages), so the tab is
    // everyone's: a member opens the chart, a client viewer the scorecard.
    id: "mode:kpis",
    publisher: "companyos",
    key: "kpis",
    label: "KPIs",
    icon: GaugeIcon,
    path: "/c/$slug/kpis",
    feature: "core",
    overlayFeatures: ["kpi"],
    roles: EVERYONE,
    defaultFor: [],
    shortcut: "k",
    digit: "5",
    mobileOrder: 3,
    placement: "tab",
    sidebar: KpisSidebar,
  })
  .register("mode", {
    // The digital workforce (note §3): the DEs, their profiles, time, cost and lifecycle are
    // `core` today; runs, approvals and crews come with the `agents` add-on (M3, M4).
    id: "mode:agents",
    publisher: "companyos",
    key: "agents",
    // WORKFORCE (2026-10-26): digital employees (DE) and agents (AG); the key and path stay.
    // CONNECTIONS-AREA (founder decision 2026-10-30): the rail item is "Team".
    label: "Team",
    icon: BotIcon,
    path: "/c/$slug/agents",
    feature: "core",
    overlayFeatures: ["agents"],
    roles: EVERYONE,
    defaultFor: [],
    shortcut: "a",
    digit: "6",
    mobileOrder: 4,
    // NAV-REDESIGN (2026-10-29): people meet DEs in Chat; Workforce (add, remove, manage them)
    // sits at the rail's foot, above Settings. Its URL is unchanged.
    placement: "foot",
    sidebar: AgentsSidebar,
  })
  .register("mode", {
    id: "mode:reports",
    publisher: "companyos",
    key: "reports",
    label: "Reports",
    icon: FileTextIcon,
    // NAV-REDESIGN: a section of KPIs (`/reports/…` redirects); key 7 and `g r` stay.
    path: "/c/$slug/kpis/reports",
    // KPIS-MODE (2026-10-22): Reports holds the Friday reports (`kpi`, the `report-weeks` gate)
    // and Finance (`finance` gate, its own add-on rules on each page), so the mode is `core`.
    feature: "core",
    overlayFeatures: [],
    roles: READERS,
    // KPIS-MODE: not a plain member's tab (a fresh member has nothing in it); a seat holder
    // still opens its own budget and forecast by link (the chart's cost lens, the seat page),
    // the `finance` gate's rule.
    defaultFor: ["client_viewer"],
    shortcut: "r",
    digit: "7",
    mobileOrder: 5,
    placement: "section",
    sidebar: ReportsSidebar,
  })
  .register("mode", {
    id: "mode:setup",
    publisher: "companyos",
    key: "setup",
    // NAV-REDESIGN: named Settings everywhere (rail, titles, breadcrumbs); key and path stay.
    label: "Settings",
    icon: SettingsIcon,
    path: "/c/$slug/setup",
    feature: "core",
    overlayFeatures: [],
    roles: MANAGERS,
    defaultFor: [],
    shortcut: ",",
    mobileOrder: 9,
    // NAV-REDESIGN: the rail's Settings, just above Me.
    placement: "foot",
    sidebar: SetupSidebar,
  });

export function allModes(): ModeEntry[] {
  return extensionPoints.list("mode");
}

export function modeByKey(key: string): ModeEntry | undefined {
  return allModes().find((m) => m.key === key);
}

/**
 * A page audience inside a mode (KPIS-MODE): the rules a route's `ModeGate` holds it to when
 * they are not its mode's. No tab, key or sidebar of its own.
 */
export type ModeRules = Pick<
  ModeEntry,
  "key" | "label" | "feature" | "roles" | "openRoles" | "companyKinds"
>;

/**
 * The gates (KPIS-MODE, 2026-10-22). KPIs is everyone's tab; its pages keep the audiences they
 * had as two modes: `chart` (the org chart, a seat, Priorities, Issues, Meetings, Vision: every
 * member), `cadence` (the scorecard grid, published weeks, a DE's scorecard: the company's KPI
 * readers behind `kpi`; a plain member opens only its own pages, `ModeGate open`). Reports keeps
 * the Friday reports behind `kpi` (`report-weeks`) and Finance's pages (moved from Chart) for
 * every member, whose own rows the finance read cuts (`finance`).
 */
export const GATES: readonly ModeRules[] = [
  { key: "chart", label: "Chart", feature: "core", roles: MEMBERS },
  { key: "cadence", label: "Scorecard", feature: "kpi", roles: READERS, openRoles: ["member"] },
  { key: "report-weeks", label: "Reports", feature: "kpi", roles: READERS },
  { key: "finance", label: "Finance", feature: "core", roles: MEMBERS },
];

/** A mode or a gate by key: what `ModeGate` and the page gates check. */
export function rulesByKey(key: string): ModeRules | undefined {
  return modeByKey(key) ?? GATES.find((g) => g.key === key);
}

/** Whether the user passes a mode's or gate's rules (`open`: its open routes too). */
export function canOpen(
  key: string,
  me: MeResponse,
  company: CompanyAccess,
  open = false,
): boolean {
  const rules = rulesByKey(key);
  if (!rules) return false;
  if (!open && openOnly(rules, company)) return false;
  return modeAccess(rules, me, company) === "ok";
}

export type ModeAccess = "ok" | "role" | "feature";

/** Why a mode can or cannot be opened by this user in this company. */
export function modeAccess(mode: ModeRules, me: MeResponse, company: CompanyAccess): ModeAccess {
  // FOUNDER-OS: a mode made for one kind of company (Portfolio: Founder OS only).
  if (mode.companyKinds && !mode.companyKinds.includes(company.kind)) return "role";
  if (!mode.roles.some((r) => company.roles.includes(r)) && !openOnly(mode, company)) return "role";
  if (!featureState(me, company.id, mode.feature).enabled) return "feature";
  return "ok";
}

/**
 * The user reaches the mode only through its `openRoles` (no role of `roles`): only the routes
 * marked open (`ModeGate open`) are its.
 */
export function openOnly(mode: ModeRules, company: CompanyAccess): boolean {
  return (
    !mode.roles.some((r) => company.roles.includes(r)) &&
    (mode.openRoles ?? []).some((r) => company.roles.includes(r))
  );
}

/** Modes the user can open: allowed by role and enabled by entitlement. */
export function visibleModes(me: MeResponse, company: CompanyAccess): ModeEntry[] {
  return allModes().filter((m) => modeAccess(m, me, company) === "ok");
}

/**
 * Roles that see an add-on mode locked rather than absent (design spec §3, §12.1). Agency
 * operators too: they are the ones who can switch the add-on on (coverage audit §5 row 4).
 */
const ADDON_MANAGERS: readonly CompanyRole[] = MANAGERS;

export type ShownMode = { mode: ModeEntry; state: "ok" | "locked" | "read-only" };

/**
 * Every mode item the user sees (tabs, rail, phone bar), one check (`modeAccess` +
 * `featureState`): a role that may not open the mode never sees it; an add-on that is off hides
 * it from members and shows it locked to owners and admins; a grace period marks it read-only.
 */
export function shownModes(me: MeResponse, company: CompanyAccess): ShownMode[] {
  return allModes().flatMap((mode): ShownMode[] => {
    const access = modeAccess(mode, me, company);
    if (access === "role") return [];
    if (access === "feature") {
      return company.roles.some((r) => ADDON_MANAGERS.includes(r))
        ? [{ mode, state: "locked" }]
        : [];
    }
    return [
      { mode, state: featureState(me, company.id, mode.feature).readOnly ? "read-only" : "ok" },
    ];
  });
}

/** First landing (note §4): the role default, else the first openable tab, else anything. */
export function defaultMode(me: MeResponse, company: CompanyAccess): ModeEntry | null {
  // KPIS-MODE: Reports is core now, but a client viewer lands there for the Friday reports; with
  // `kpi` off it would open on "not enabled", so the landing falls through to Home.
  const visible = visibleModes(me, company).filter(
    (m) => m.key !== "reports" || canOpen("report-weeks", me, company),
  );
  const mine = (m: ModeEntry) => m.defaultFor.some((r) => company.roles.includes(r));
  return (
    // FOUNDER-OS: a mode made for this kind of company lands first (Portfolio in Founder OS).
    visible.find((m) => m.companyKinds?.includes(company.kind) && mine(m)) ??
    visible.find((m) => m.defaultFor.some((r) => company.roles.includes(r))) ??
    visible.find((m) => m.placement === "global") ??
    visible.find((m) => m.placement === "tab") ??
    visible[0] ??
    null
  );
}
